import { BigNumber } from "@/components/dashboard/big-number";
import { ConcentrationList } from "@/components/dashboard/concentration-list";
import { InsightCard } from "@/components/dashboard/insight-card";
import { MarginLeaderList } from "@/components/dashboard/margin-leader-list";
import { RevenueSparkline } from "@/components/dashboard/revenue-sparkline";
import { YtdStats } from "@/components/dashboard/ytd-stats";
import { DashboardFilters } from "@/features/dashboard/DashboardFilters";
import { useDashboardFilters } from "@/features/dashboard/useDashboardFilters";
import { useSession } from "@/lib/auth-client";
import { trpc } from "@/lib/trpc";
import { dashboardFiltersSchema, formatDollars } from "@payrollmart-invoice/shared";
import { Link, createFileRoute } from "@tanstack/react-router";
import { ClipboardCheck, TrendingUp, Upload } from "lucide-react";

export const Route = createFileRoute("/_app/")({
	component: OverviewPage,
	validateSearch: dashboardFiltersSchema,
});

function OverviewPage() {
	// Phase 8 D2: filter state is in URL search params, parsed + defaulted by
	// `validateSearch` above. The hook reads + writes those params; passing
	// `filters` straight into the 4 dashboard procedures lets TanStack Query
	// re-key the cache on every filter change.
	const { filters, setFilters, reset } = useDashboardFilters();
	const { data: overview } = trpc.invoices.overviewSummary.useQuery(filters);
	const { data: trend } = trpc.invoices.revenueTrend.useQuery(filters);
	const { data: concentration } = trpc.invoices.customerConcentration.useQuery(filters);
	const { data: summary } = trpc.invoices.dashboardSummary.useQuery(filters);
	const { data: sessionData } = useSession();

	const draftCount = overview?.draftsToReview ?? summary?.draftsToReview ?? 0;
	const firstName = getFirstName(sessionData?.user?.name);
	const greeting = buildGreeting(firstName, overview?.today);
	const subCopy = buildSubCopy(overview, draftCount);
	const prevDelta = buildPrevMonthDelta(overview);
	const marginDelta = buildMarginDelta(overview);
	const concentrationHint = buildConcentrationHint(concentration);
	const lowestMargin = summary?.bottomCustomers?.[0] ?? null;

	return (
		<div className="space-y-6" data-testid="overview-page">
			{/* Page header */}
			<div className="flex flex-wrap items-start justify-between gap-4">
				<div>
					<h1 className="text-xl font-semibold text-[#1e1e20]" data-testid="overview-greeting">
						{greeting}
					</h1>
					<p className="mt-1 max-w-2xl text-sm text-[#6b7280]">{subCopy}</p>
				</div>
				<div className="flex gap-2">
					<Link
						to="/documents"
						className="inline-flex items-center gap-1.5 rounded-md border border-[#e5e7eb] bg-white px-3.5 py-2 text-sm font-medium text-[#1e1e20] transition-colors hover:border-brand-primary hover:text-brand-primary"
					>
						<Upload className="h-4 w-4" strokeWidth={1.5} />
						Upload documents
					</Link>
					<Link
						to="/invoices/review"
						className="inline-flex items-center gap-1.5 rounded-md bg-brand-primary px-3.5 py-2 text-sm font-medium text-white transition-colors hover:bg-brand-primary-hover"
					>
						<ClipboardCheck className="h-4 w-4" strokeWidth={1.5} />
						Review drafts
						{draftCount > 0 && (
							<span className="ml-0.5 rounded-full bg-white/20 px-1.5 py-0.5 text-[11px] font-semibold leading-none">
								{draftCount}
							</span>
						)}
					</Link>
				</div>
			</div>

			{/* Filter bar — Phase 8 */}
			<DashboardFilters value={filters} onChange={setFilters} onReset={reset} />

			{/* Hero row */}
			<div className="grid gap-3 md:grid-cols-[2fr_1fr_1fr]">
				<BigNumber
					label={overview?.monthLabel ? `${overview.monthLabel} revenue` : "This month revenue"}
					value={overview ? formatDollars(overview.mtd.revenueCents) : "—"}
					sub={
						overview && overview.mtd.invoiceCount > 0 ? (
							<span>
								Across{" "}
								<strong className="text-[#1e1e20]">
									{overview.mtd.invoiceCount}{" "}
									{overview.mtd.invoiceCount === 1 ? "invoice" : "invoices"}
								</strong>
								. {prevDelta}
							</span>
						) : overview ? (
							<span>No invoices billed this month yet.</span>
						) : null
					}
					foot={
						overview && overview.mtd.pctBilled > 0
							? `${overview.mtd.pctBilled}% of active customers billed so far.`
							: null
					}
					accent
				/>
				<BigNumber
					label="Gross margin"
					value={overview ? `${overview.mtd.marginPercent}%` : "—"}
					sub={
						overview && overview.mtd.revenueCents > 0 ? (
							<span>
								<strong className="text-[#1e1e20]">
									{formatDollars(overview.mtd.grossProfitCents)}
								</strong>{" "}
								kept after payroll costs.
							</span>
						) : null
					}
					foot={marginDelta}
				/>
				<BigNumber
					label="Active customers"
					value={overview ? String(overview.activeCustomers) : "—"}
					sub={concentrationHint}
					foot={
						overview && overview.ytd.newCustomers > 0
							? `${overview.ytd.newCustomers} added in ${overview.yearLabel}.`
							: overview
								? "No churn so far this year."
								: null
					}
				/>
			</div>

			{/* Revenue trend */}
			<div className="rounded-lg border border-[#e5e7eb] bg-white">
				<div className="flex items-center justify-between border-b border-[#e5e7eb] px-6 py-4">
					<div className="flex items-center gap-2">
						<TrendingUp className="h-4 w-4 text-[#6b7280]" strokeWidth={1.5} />
						<h2 className="text-sm font-semibold text-[#1e1e20]">
							Revenue vs. source charges, last 12 months
						</h2>
					</div>
				</div>
				<div className="px-6 pb-4 pt-5">
					{trend ? (
						<RevenueSparkline points={trend} />
					) : (
						<div className="h-[140px] animate-pulse rounded bg-[#f8f9fb]" />
					)}
				</div>
			</div>

			{/* Concentration + Margin leaders */}
			<div className="grid gap-3 lg:grid-cols-2">
				{concentration ? <ConcentrationList data={concentration} /> : <CardSkeleton />}
				{summary ? (
					<MarginLeaderList
						title="Margin leaders"
						customers={summary.topCustomers}
						warnBelowPct={30}
					/>
				) : (
					<CardSkeleton />
				)}
			</div>

			{/* YTD + Insight */}
			<div className="grid gap-3 lg:grid-cols-2">
				{overview ? <YtdStats overview={overview} /> : <CardSkeleton />}
				{overview ? (
					<InsightCard
						lowestMargin={lowestMargin}
						mtdRevenueCents={overview.mtd.revenueCents}
						ytdRevenueCents={overview.ytd.revenueCents}
						monthLabel={overview.monthLabel}
						yearLabel={overview.yearLabel}
					/>
				) : (
					<CardSkeleton />
				)}
			</div>
		</div>
	);
}

function CardSkeleton() {
	return <div className="h-48 animate-pulse rounded-lg border border-[#e5e7eb] bg-[#f8f9fb]" />;
}

/** Extracts the first word of the user's `name` field for a greeting. */
function getFirstName(fullName: string | null | undefined): string | null {
	if (!fullName) return null;
	const trimmed = fullName.trim();
	if (trimmed.length === 0) return null;
	return trimmed.split(/\s+/)[0] ?? null;
}

/**
 * Greeting for the Overview header. Reads the user's first name from the
 * session so invited admins/staff see their own name. The time-of-day phrase
 * comes from the client clock, which is fine for a decorative greeting —
 * the user's own machine is authoritative for "morning/afternoon/evening".
 * When `today` is undefined (query still loading) we short-circuit to the
 * bare "Overview" label so we don't flash a half-rendered greeting.
 */
function buildGreeting(firstName: string | null, today: string | undefined): string {
	if (!today) return "Overview";
	const hour = new Date().getHours();
	const when = hour < 12 ? "Good morning" : hour < 18 ? "Good afternoon" : "Good evening";
	return firstName ? `${when}, ${firstName}.` : `${when}.`;
}

function buildSubCopy(
	overview: { mtd: { pctBilled: number }; monthLabel: string } | undefined,
	draftCount: number,
): string {
	if (!overview) return "Your revenue, customers, and this month's progress at a glance.";
	const pct = overview.mtd.pctBilled;
	const pctFragment =
		pct === 0
			? `${overview.monthLabel} hasn't started billing yet`
			: `${overview.monthLabel} is ${pct}% billed`;
	if (draftCount === 0) return `${pctFragment}. No drafts waiting on review.`;
	const noun = draftCount === 1 ? "draft needing review" : "drafts needing review";
	return `${pctFragment}. You have ${draftCount} ${noun}.`;
}

function buildPrevMonthDelta(
	overview:
		| {
				mtd: { revenueCents: number };
				prevMonth: { revenueCents: number };
		  }
		| undefined,
): string {
	if (!overview) return "";
	const { revenueCents: current } = overview.mtd;
	const { revenueCents: prev } = overview.prevMonth;
	if (prev === 0) return "";
	const diff = current - prev;
	if (diff === 0) return "Pace matches last month.";
	const direction = diff > 0 ? "Up" : "Down";
	return `${direction} from ${formatDollars(prev)} last month.`;
}

function buildMarginDelta(
	overview:
		| {
				mtd: { marginPercent: number; revenueCents: number };
				prevMonth: { marginPercent: number; revenueCents: number };
		  }
		| undefined,
): string | null {
	// Guard both sides. Server reports marginPercent = 0 when revenue = 0
	// (no NaN), so without the mtd guard the 1st-of-month card would show
	// "Down N points from last month" when really nothing has billed yet.
	if (!overview || overview.prevMonth.revenueCents === 0 || overview.mtd.revenueCents === 0) {
		return null;
	}
	const delta = overview.mtd.marginPercent - overview.prevMonth.marginPercent;
	if (delta === 0) return "Flat from last month.";
	const direction = delta > 0 ? "Up" : "Down";
	const points = Math.abs(delta) === 1 ? "point" : "points";
	return `${direction} ${Math.abs(delta)} ${points} from last month.`;
}

function buildConcentrationHint(
	concentration:
		| {
				rows: Array<{ customerName: string; pctOfTotal: number; isOther: boolean }>;
		  }
		| undefined,
): string | null {
	if (!concentration) return null;
	const leader = concentration.rows.find((r) => !r.isOther);
	if (!leader || leader.pctOfTotal <= 0) return "No revenue this month yet.";
	return `${leader.customerName} is ${leader.pctOfTotal.toFixed(0)}% of this month's revenue.`;
}
